import { cva, type VariantProps } from 'class-variance-authority'
import { MinusIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

const optionButtonVariants = cva(
  'rounded-lg border-2 flex items-center justify-center font-semibold',
  {
    defaultVariants: {
      size: 'default',
    },
    variants: {
      size: {
        default: 'h-10 min-w-14 px-3 text-xs',
        large: 'h-14 min-w-20 px-5 text-base',
      },
    },
  },
)

const optionLabelVariants = cva('truncate', {
  defaultVariants: {
    size: 'default',
  },
  variants: {
    size: {
      default: 'max-w-[14ch]',
      large: 'max-w-[28ch]',
    },
  },
})

const optionIconVariants = cva('', {
  defaultVariants: {
    size: 'default',
  },
  variants: {
    size: {
      default: 'size-5',
      large: 'size-7',
    },
  },
})

export type OptionButtonProps = {
  text: string
  selected: boolean
  /** Renders a dash instead of the label and uses the muted selected styling. */
  isNotRelevant?: boolean
  className?: string
  onClick(): void
} & VariantProps<typeof optionButtonVariants>

/**
 * A single selectable choice rendered as a bordered button — used for checkpoint
 * single-choice questions and for picking a pause reason.
 */
export const OptionButton = ({
  text,
  selected,
  isNotRelevant,
  size,
  className,
  onClick,
}: OptionButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    title={text}
    className={cn(
      optionButtonVariants({ size }),
      selected
        ? isNotRelevant
          ? 'bg-neutral-200 dark:bg-neutral-700 border-neutral-400 dark:border-neutral-500 text-text-primary'
          : 'bg-interactive/15 border-interactive text-interactive'
        : 'border-strong-01 bg-field-2 text-text-secondary hover:bg-field-2-hover hover:border-interactive',
      className,
    )}
  >
    {isNotRelevant ? (
      <MinusIcon className={optionIconVariants({ size })} />
    ) : (
      <span className={optionLabelVariants({ size })}>{text}</span>
    )}
  </button>
)
